<script lang="ts">
export default {
  name: 'EditableField',
};
</script>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { ParsedField } from '@/types/field';
import type { FieldOverride } from '@/types/ai';
import { Loader2, Undo2, AlertCircle } from 'lucide-vue-next';
import { TooltipRoot, TooltipTrigger, TooltipContent, TooltipProvider, TooltipPortal } from 'reka-ui';

const props = defineProps<{
  field: ParsedField;
  override: FieldOverride | null;
  isPending: boolean;
  error: string | null;
  hasLocalChange?: boolean;
}>();

const emit = defineEmits<{
  'update:value': [value: string | number | boolean];
  revert: [];
  'clear-error': [];
}>();

// Local state for the input value
const localValue = ref<string | number | boolean>('');

// Debounce timer
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

// Initialize local value from override or original field value
const initializeValue = () => {
  if (props.override) {
    localValue.value = props.override.overrideValue;
  } else {
    localValue.value = props.field.value as string | number | boolean;
  }
};

// Initialize on mount and when field/override changes
watch(
  () => [props.field.path, props.override],
  () => {
    initializeValue();
  },
  { immediate: true }
);

// Reinitialize when local change is reverted (hasLocalChange goes from true to false)
watch(
  () => props.hasLocalChange,
  (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
      initializeValue();
    }
  }
);

// Computed
const hasOverride = computed(() => props.override !== null);
const hasUnsavedChange = computed(() => props.hasLocalChange === true);
const hasAnyChange = computed(() => hasOverride.value || hasUnsavedChange.value);

const originalValue = computed(() => {
  if (props.override) {
    return props.override.originalValue;
  }
  return props.field.value;
});

const displayOriginalValue = computed(() => {
  const val = originalValue.value;
  if (val === null || val === undefined) return 'N/A';
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  return String(val);
});

// Handle input changes with debounce
const handleInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let newValue: string | number | boolean;

  if (props.field.type === 'number') {
    newValue = target.value === '' ? 0 : Number(target.value);
  } else {
    newValue = target.value;
  }

  localValue.value = newValue;

  // Clear any existing error when user starts typing
  if (props.error) {
    emit('clear-error');
  }

  // Debounce the save
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    // Only emit if value changed from original/override
    const currentStored = props.override ? props.override.overrideValue : props.field.value;
    if (newValue !== currentStored) {
      emit('update:value', newValue);
    }
  }, 500);
};

// Handle boolean toggle
const handleToggleChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const newValue = target.checked;
  localValue.value = newValue;

  // Clear any existing error
  if (props.error) {
    emit('clear-error');
  }

  // Emit immediately for boolean (no debounce needed)
  emit('update:value', newValue);
};

// Handle revert
const handleRevert = () => {
  localValue.value = originalValue.value as string | number | boolean;
  emit('revert');
};
</script>

<template>
  <div class="tw:flex tw:w-full tw:flex-col tw:gap-1">
    <div class="tw:flex tw:items-center tw:gap-2">
      <!-- String/Number Input -->
      <div
        v-if="field.type === 'string' || field.type === 'number'"
        class="tw:relative tw:flex-1">
        <TooltipProvider>
          <TooltipRoot :disabled="!hasAnyChange">
            <TooltipTrigger as-child>
              <input
                :type="field.type === 'number' ? 'number' : 'text'"
                :value="localValue"
                @input="handleInputChange"
                :disabled="isPending"
                :class="[
                  'tw:w-full tw:rounded-md tw:border tw:px-2 tw:py-1 tw:text-sm tw:outline-none tw:transition-colors',
                  'focus:tw:ring-1',
                  hasUnsavedChange
                    ? 'tw:border-amber-500 tw:bg-amber-50 focus:tw:border-amber-600 focus:tw:ring-amber-500'
                    : hasOverride
                      ? 'tw:border-green-500 tw:bg-green-50 focus:tw:border-green-600 focus:tw:ring-green-500'
                      : 'tw:border-slate-300 tw:bg-white focus:tw:border-blue-500 focus:tw:ring-blue-500',
                  error ? 'tw:border-red-500 tw:bg-red-50' : '',
                  isPending ? 'tw:opacity-60' : '',
                ]" />
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent
                v-if="hasAnyChange"
                side="top"
                :side-offset="5"
                class="tw:z-50 tw:max-w-xs tw:rounded-md tw:bg-slate-800 tw:px-3 tw:py-2 tw:text-xs tw:text-white tw:shadow-lg">
                <p class="tw:font-medium">Original value:</p>
                <p class="tw:mt-1 tw:break-words tw:text-slate-300">{{ displayOriginalValue }}</p>
              </TooltipContent>
            </TooltipPortal>
          </TooltipRoot>
        </TooltipProvider>
      </div>

      <!-- Boolean Toggle -->
      <div
        v-else-if="field.type === 'boolean'"
        class="tw:flex tw:flex-1 tw:items-center tw:gap-2">
        <TooltipProvider>
          <TooltipRoot :disabled="!hasAnyChange">
            <TooltipTrigger as-child>
              <label
                :class="[
                  'tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:rounded-md tw:border tw:px-2 tw:py-1',
                  hasUnsavedChange
                    ? 'tw:border-amber-500 tw:bg-amber-50'
                    : hasOverride
                      ? 'tw:border-green-500 tw:bg-green-50'
                      : 'tw:border-slate-300 tw:bg-white',
                  isPending ? 'tw:opacity-60' : '',
                ]">
                <input
                  type="checkbox"
                  :checked="localValue === true"
                  @change="handleToggleChange"
                  :disabled="isPending"
                  class="tw:toggle tw:toggle-sm tw:toggle-primary" />
                <span class="tw:text-sm tw:text-slate-600">
                  {{ localValue ? 'true' : 'false' }}
                </span>
              </label>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent
                v-if="hasAnyChange"
                side="top"
                :side-offset="5"
                class="tw:z-50 tw:max-w-xs tw:rounded-md tw:bg-slate-800 tw:px-3 tw:py-2 tw:text-xs tw:text-white tw:shadow-lg">
                <p class="tw:font-medium">Original value:</p>
                <p class="tw:mt-1 tw:text-slate-300">{{ displayOriginalValue }}</p>
              </TooltipContent>
            </TooltipPortal>
          </TooltipRoot>
        </TooltipProvider>
      </div>

      <!-- Loading Spinner -->
      <Loader2
        v-if="isPending"
        class="tw:h-4 tw:w-4 tw:shrink-0 tw:animate-spin tw:text-blue-500" />

      <!-- Revert Button (shown when there's a local change or saved override) -->
      <button
        v-if="hasAnyChange && !isPending"
        @click="handleRevert"
        class="tw:flex tw:h-7 tw:w-7 tw:shrink-0 tw:items-center tw:justify-center tw:rounded tw:text-slate-500 tw:transition-colors tw:hover:bg-slate-100 tw:hover:text-slate-700"
        :title="hasUnsavedChange ? 'Discard change' : 'Revert to original value'">
        <Undo2 class="tw:h-4 tw:w-4" />
      </button>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-red-600">
      <AlertCircle class="tw:h-3 tw:w-3" />
      <span>{{ error }}</span>
    </div>
  </div>
</template>
